import { useState, useMemo, useEffect, useCallback } from 'react';
import { MenuUnfoldOutlined, MenuFoldOutlined } from '@ant-design/icons';
import { Layout, Menu, Button, Popover, Drawer, Grid, Skeleton } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/useAuthStore';
import { useUpdateLanguage } from '../Hooks/Index';
import { useSettings } from '../Context/SettingsContext';
import { useSafeTranslation } from '../Hooks/useSafeTranslation';
import ChangePasswordModal from '../components/Modals/ChangePasswordModal';
import { SelectOperatorDropdown } from '../components/Navbar/SelectClientDropdown';
import QuickChargerSearch from '../components/Navbar/QuickChargerSearch';
import OCPPHealthCheck from '../components/HealthCheck/OCPPHealthCheck';
// React Icons - Professional & Consistent for Turkey EV Portal
import {
  MdPieChart,
  MdBusiness,
  MdCreditCard,
  MdEvStation,
  MdLocationOn,
  MdHistory,
  MdGroups,
  MdPersonSearch,
  MdAssessment,
  MdTerminal,
  MdChecklist,
  MdSettings,
  MdBuild,
  MdPerson,
  MdSchedule,
  MdReceiptLong,
  MdDirectionsCar,
  MdNotifications,
} from 'react-icons/md';
import { UserDropdown } from '../components/Navbar/UserMenu';

const { Header, Sider, Content } = Layout;

const siderStyle = {
  overflow: 'auto',
  height: '100vh',
  position: 'sticky',
  insetInlineStart: 0,
  top: 0,
  bottom: 0,
  scrollbarWidth: 'thin',
  scrollbarGutter: 'stable',
};

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const { settings = {}, isloading = false, error } = useSettings();

  const { user, setCredentials, token } = useAuthStore();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('dashboard');
  const { mutate: updateLanguage } = useUpdateLanguage();
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    if (user?.preferredLanguage) {
      const lowerCaseLang = user.preferredLanguage.toLowerCase();
      i18n.changeLanguage(lowerCaseLang);
    }
  }, [user?.preferredLanguage, i18n]);

  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [isMobile]);

  const handleLanguage = (lang) => {
    const capitalizedLang = lang.toUpperCase();
    i18n.changeLanguage(lang);
    updateLanguage(
      { preferredLanguage: capitalizedLang },
      {
        onSuccess: () => {
          setPopoverOpen(false);
          setCredentials(token, {
            ...user,
            preferredLanguage: capitalizedLang,
          });
        },
      }
    );
  };

  // const handleFCMToken = async () => {
  //   const token = await getDeviceToken();
  //   console.log(token);
  // }
  // Define menu items function to avoid duplication
  const getAllMenuItems = useCallback(() => [
    // 📊 Overview
    {
      key: '/dashboard',
      icon: <MdPieChart size={22} />,
      label: t('dashboard'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'LOCATION_OWNER', 'READ_ONLY_ADMIN'],
    },

    // 👥 People & Access
    {
      key: '/dashboard/operators',
      icon: <MdBusiness size={22} />,
      label: t('host_management'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'READ_ONLY_ADMIN'],
    },

    {
      key: '/dashboard/locations',
      icon: <MdLocationOn size={22} />,
      label: t('stations'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'LOCATION_OWNER', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/chargers',
      icon: <MdEvStation size={22} />,
      label: t('chargers'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'LOCATION_OWNER', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/teams',
      icon: <MdGroups size={22} />,
      label: t('teamManagement'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/locationsOwners',
      icon: <MdPersonSearch size={22} />,
      label: t('locationOwners'),
      roles: ['OPERATOR',],
    },
    {
      key: '/dashboard/customers',
      icon: <MdPerson size={22} />,
      label: t('customers'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY_ADMIN'],
    },

    // ⚡ Operations
    {
      key: '/dashboard/rfid',
      icon: <MdCreditCard size={22} />,
      label: t('rfid_management'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/rfid-transactions',
      icon: <MdReceiptLong size={22} />,
      label: t('rfid_transactions'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/ev-vehicle-models',
      icon: <MdDirectionsCar size={22} />,
      label: t('ev_vehicle_models'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY_ADMIN'],
    },

    {
      key: '/dashboard/charging-history',
      icon: <MdHistory size={22} />,
      label: t('charging_history'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY_ADMIN'],
    },

    // 📈 Insights
    {
      key: '/dashboard/reports',
      icon: <MdAssessment size={22} />,
      label: t('reports'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'LOCATION_OWNER', 'READ_ONLY_ADMIN'],
    },

    // 📑 Logs & Audit
    {
      key: '/dashboard/audit',
      icon: <MdChecklist size={22} />,
      label: t('logs'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/OCPP-logs',
      icon: <MdTerminal size={22} />,
      label: t('ocppLogs'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/cron-list',
      icon: <MdSchedule size={22} />,
      label: t('cronJobs'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'READ_ONLY_ADMIN'],
    },


    // ⚙️ Administration
    {
      key: '/dashboard/diagnostic',
      icon: <MdBuild size={22} />,
      label: t('Diagnostic'),
      roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'LOCATION_OWNER', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/notifications',
      icon: <MdNotifications size={22} />,
      label: t('notifications'),
      roles: ['SUPER_ADMIN', 'READ_ONLY_ADMIN'],
    },
    {
      key: '/dashboard/settings',
      icon: <MdSettings size={22} />,
      label: t('settings'),
      roles: ['SUPER_ADMIN'],
    },
  ], [t]);

  const menuItems = useMemo(() => {
    const byRole = (item) => item.roles?.includes(user?.role);
    const allMenuItems = getAllMenuItems();

    return allMenuItems
      .filter(byRole)
      .map(item => item.children
        ? { ...item, children: item.children.filter(byRole) } // filter children by role too
        : item
      )
      .filter(item => !item.children || item.children.length > 0); // drop empty parents
  }, [user?.role, getAllMenuItems]);

  // Use safe translation for appTitle
  const safeAppTitle = useSafeTranslation('appTitle', 'Dashboard');

  // Compute selected keys and open keys for proper menu highlighting
  const { openKeys, selectedKeys, currentTitle } = useMemo(() => {
    const selected = [location.pathname];

    // Normalize pathname to remove trailing slash for consistent matching
    const normalizedPath = location.pathname.endsWith('/') && location.pathname !== '/'
      ? location.pathname.slice(0, -1)
      : location.pathname;

    // Find parent whose children contains the current path
    const complianceParentKey = menuItems.find(
      i => i.children?.some(ch => ch.key === normalizedPath)
    )?.key;

    const open = complianceParentKey ? [complianceParentKey] : [];

    // Compute current title - use getAllMenuItems for title lookup to avoid role filtering issues
    const allMenuItems = getAllMenuItems();

    const direct = allMenuItems.find(i => i.key === normalizedPath)?.label;
    if (direct) {
      return { openKeys: open, selectedKeys: selected, currentTitle: direct };
    }

    const parent = allMenuItems.find(i => i.children?.some(ch => ch.key === normalizedPath));
    if (parent) {
      const childLabel = parent.children.find(ch => ch.key === normalizedPath)?.label;
      return { openKeys: open, selectedKeys: selected, currentTitle: childLabel };
    }

    // Use safe translation for fallback
    return { openKeys: open, selectedKeys: selected, currentTitle: safeAppTitle };
  }, [location.pathname, menuItems, getAllMenuItems, safeAppTitle]);

  return (
    <Layout className="!overflow-hidden [&::-webkit-scrollbar]:!w-2  [&::-webkit-scrollbar-track]:!rounded-full   [&::-webkit-scrollbar-track]:!bg-gray-100   [&::-webkit-scrollbar-thumb]:!rounded-full [&::-webkit-scrollbar-thumb]:!bg-gray-300 dark:![&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500">
      {!isMobile ? (
        <Sider
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={siderStyle}
          className={`!border-none !overflow-hidden !bg-[#F4F4F5] ${!collapsed ? '!min-w-[230px]' : ''}`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-center pt-2 pb-2">
              {isloading ? (
                <Skeleton.Image active style={{ width: '64px', height: '64px' }} />
              ) : (
                <img
                  src={settings?.companyLogoPath || '/placeholder.webp'}
                  alt="logo"
                  className="h-auto max-h-16 object-contain min-h-16 rounded-lg"
                />
              )}
            </div>
            <div className="flex-1 overflow-y-auto hide-scrollbar">
              <Menu
                selectedKeys={selectedKeys}
                defaultOpenKeys={openKeys}
                className="!border-none !bg-[#F4F4F5] "
                inlineCollapsed={collapsed}
                items={menuItems}

                mode="inline"
                onClick={({ key }) => {
                  // Only navigate if it's not a parent menu item
                  if (key !== 'compliance-management') {
                    if (key === '/dashboard/OCPP-logs') {
                      navigate({
                        pathname: key,
                        search: '',
                        state: {},
                      });
                    } else {
                      navigate(key);
                    }
                  }
                }}
              />
            </div>
          </div>
        </Sider>
      ) : (
        <Drawer
          maskClosable={true}
          placement="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          styles={{ body: { padding: 0 } }}
          width={isMobile ? '100%' : 300}
          title={
            isloading ? (
              <div className="flex items-center justify-center mr-10">

                <Skeleton.Image active className="w-10 h-auto max-h-12 object-contain rounded-lg" />
              </div>
            ) : (
              <div className="flex items-center justify-center mr-10 rounded-lg">
                <img
                  src={error ? '/placeholder.webp' : (settings?.companyLogoPath || '/placeholder.webp')}
                  alt="logo"
                  className="w-12 h-auto max-h-12 object-contain rounded-lg"
                />
              </div>
            )
          }
        >
          <Menu
            selectedKeys={selectedKeys}
            defaultOpenKeys={openKeys}
            items={menuItems}
            mode="inline"
            className="!border-none"
            onClick={({ key }) => {
              // Only navigate if it's not a parent menu item
              if (key !== 'compliance-management') {
                if (key === '/dashboard/OCPP-logs') {
                  navigate({
                    pathname: key,
                    search: '',
                    state: {},
                  });
                } else {
                  navigate(key);
                }
                setMobileDrawerOpen(false);
              }
            }}
          />
        </Drawer>
      )}

      <Layout className="!bg-white !overflow-hidden">
        <Header className="!bg-white !shadow-lg !shadow-primary flex flex-row justify-between flex-1 !w-full items-center md:!pr-4 !p-0 !px-2">
          <div className="flex justify-between items-center">
            <div className="flex flex-row items-center gap-4">
              {isMobile ? (
                <Button
                  type="text"
                  icon={<MenuUnfoldOutlined />}
                  aria-label="Open navigation"
                  onClick={() => {
                    setMobileDrawerOpen(true);
                  }}
                />
              ) : (
                <Button
                  type="text"
                  icon={
                    collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                  }
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  onClick={() => {
                    setCollapsed(!collapsed);
                  }}
                />
              )}
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-primary md:block hidden lg:flex items-center">
                  {currentTitle}
                </h2>
              </div>
            </div>
          </div>
          <div className="flex-1 h-full justify-center items-center md:flex hidden">
            <QuickChargerSearch />
          </div>
          <div className="flex items-center justify-end gap-4">
            <OCPPHealthCheck />
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN' || user?.role === 'READ_ONLY_ADMIN') && (
              <SelectOperatorDropdown />
            )}
            <Popover
              content={
                <div className="flex flex-col">
                  <Button type="text" onClick={() => handleLanguage('en')}>
                    <img
                      className="w-5 h-5 rounded-full"
                      src="https://flagcdn.com/w40/us.png"
                      alt="English"
                    />
                    <span className="ml-2">English</span>
                  </Button>
                  <Button type="text" onClick={() => handleLanguage('tr')}>
                    <img
                      className="w-5 h-5 rounded-full"
                      src="https://flagcdn.com/w40/tr.png"
                      alt="Türkçe"
                    />
                    <span className="ml-2">Turkey</span>
                  </Button>
                </div>
              }
              trigger="click"
              open={popoverOpen}
              onOpenChange={setPopoverOpen}
            >
              <Button type="text">
                <img
                  className="w-6 h-6 rounded-full"
                  src={`https://flagcdn.com/w40/${i18n.language === 'en' ? 'us' : 'tr'}.png`}
                  alt="Language"
                />
              </Button>
            </Popover>
            {/* <Button type="text" onClick={handleFCMToken}>
              <MdNotifications />
            </Button> */}
            <UserDropdown />
          </div>
        </Header>

        <Content
          style={{
            height: 'calc(100vh - 74px)',
            overflow: 'auto',
          }}
          className="px-4 pt-4"
        >
          <Outlet />
        </Content>
      </Layout>

      <ChangePasswordModal
        isOpen={!user?.isPasswordChanged}
        onLogout={() => clearAuth()}
      />
    </Layout>
  );
}
